import type { ChatClient, Session, Message, MessagePart, Bot } from './types'
import type { AdaptiveCardSchema } from '@/lib/adaptive-card'
import { greetingTemplate, greetingData } from './mockGreetingData'
import {
  acTestTemplate,
  acTestData,
  acActivityUpdateTemplate,
  acActivityUpdateData,
  acExpenseReportTemplate,
  acExpenseReportData,
} from './mockAdaptiveCardData'

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const responses = [
  "That's a great question! Let me think about that.",
  "I understand. Here's what I'd suggest...",
  "Interesting! Could you tell me more?",
  "Sure, I can help with that.",
  "Good point. Here's my take on it.",
] as const

export class MockChatClient implements ChatClient {
  private bots: Bot[] = [
    // Academic bots
    { id: 'math112-0101-fall2025', name: 'MATH112 - 0101 - Fall 2025', type: 'academic' },
    { id: 'engl101-0301-fall2025', name: 'ENGL101 - 0301 - Fall 2025', type: 'academic' },
    { id: 'cmsc132-0201-spring2025', name: 'CMSC132 - 0201 - Spring 2025', type: 'academic' },
    // Departmental bots
    { id: 'hr-bot', name: 'HR Assistant', type: 'departmental' },
    { id: 'it-support', name: 'IT Support', type: 'departmental' },
    { id: 'finance-bot', name: 'Finance Helper', type: 'departmental' },
    // Personal bots
    { id: 'general-chat', name: 'General Chat', type: 'personal' },
    { id: 'coding-buddy', name: 'Coding Buddy', type: 'personal' },
  ]

  private sessions: Session[] = [
    { id: '1', title: 'Project Planning', botId: 'coding-buddy', starred: true, createdAt: new Date('2025-01-10'), updatedAt: new Date(Date.now() - 2 * 60 * 1000) }, // 2m ago
    { id: '2', title: 'Code Review Help', botId: 'coding-buddy', createdAt: new Date('2025-01-11'), updatedAt: new Date(Date.now() - 4 * 60 * 60 * 1000) }, // 4h ago
    { id: '3', title: 'Citation Playground', botId: 'cmsc132-0201-spring2025', starred: true, createdAt: new Date('2025-01-12'), updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) }, // 3d ago
    { id: 'markdown', title: 'Markdown Playground', botId: 'engl101-0301-fall2025', createdAt: new Date('2025-01-13'), updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) }, // 1w ago
    { id: 'adaptive-cards', title: 'Adaptive Card Playground', botId: 'it-support', createdAt: new Date('2025-01-14'), updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000) }, // 1h ago
    { id: '4', title: 'Expense Questions', botId: 'finance-bot', starred: true, createdAt: new Date('2025-01-15'), updatedAt: new Date(Date.now() - 30 * 60 * 1000) }, // 30m ago
    { id: '5', title: 'Benefits Inquiry', botId: 'hr-bot', createdAt: new Date('2025-01-16'), updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) }, // 2d ago
    { id: '6', title: 'Calculus Help', botId: 'math112-0101-fall2025', createdAt: new Date('2025-01-17'), updatedAt: new Date(Date.now() - 5 * 60 * 1000) }, // 5m ago
    { id: '7', title: 'Random Ideas', botId: 'general-chat', createdAt: new Date('2025-01-18'), updatedAt: new Date(Date.now() - 10 * 60 * 1000) }, // 10m ago
  ]

  getBots(): Bot[] {
    return [...this.bots]
  }

  getBot(botId: string): Bot | undefined {
    return this.bots.find(b => b.id === botId)
  }

  private messages: Record<string, Message[]> = {
    '1': [
      {
        id: '1a',
        sessionId: '1',
        role: 'user',
        parts: [{ type: 'text', content: 'Help me plan a new feature', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: '1b',
        sessionId: '1',
        role: 'assistant',
        parts: [{ type: 'text', content: 'Sure! What kind of feature?', format: 'plain' }],
        createdAt: new Date(),
      },
    ],
    '2': [
      {
        id: '2a',
        sessionId: '2',
        role: 'user',
        parts: [{ type: 'text', content: 'Can you review this function?', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: '2b',
        sessionId: '2',
        role: 'assistant',
        parts: [{ type: 'text', content: 'Of course, paste the code.', format: 'plain' }],
        createdAt: new Date(),
      },
    ],
    '3': [
      {
        id: '3a',
        sessionId: '3',
        role: 'user',
        parts: [{
          type: 'text',
          content: "What's the impact of citations on academic writing styles? I'd like to reference multiple sources accurately.",
          format: 'plain',
        }],
        createdAt: new Date(),
      },
      {
        id: '3b',
        sessionId: '3',
        role: 'assistant',
        parts: [{
          type: 'text',
          format: 'markdown',
          content: [
            "Citations ensure traceability and credibility for critical claims [^impact-study]. ",
            "They also provide readers with context to explore background material[^style-guide], ",
            "and can be used to highlight multidisciplinary viewpoints[^multi].",
          ].join(''),
        }],
        sources: [
          { id: 'impact-study', title: 'Impact Study on Citation Usage', link: 'https://example.com/citation-impact' },
          { id: 'style-guide', title: 'Academic Writing Style Guide', link: 'https://example.com/style-guide' },
          { id: 'multi', title: 'Multidisciplinary Research Patterns', link: 'https://example.com/multi' },
        ],
        createdAt: new Date(),
      },
      {
        id: '3c',
        sessionId: '3',
        role: 'user',
        parts: [{ type: 'text', content: 'Can you show another example with footnotes?', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: '3d',
        sessionId: '3',
        role: 'assistant',
        parts: [{
          type: 'text',
          format: 'markdown',
          content: [
            "Sure! Here's a snippet:\n",
            "1. Citations improve trust.[^trust]\n",
            "2. They support reproducibility.[^repro]\n\n",
            "[^trust]: Trust Metrics 2023 — https://example.com/trust\n",
            "[^repro]: Reproducibility Handbook\n",
          ].join(''),
        }],
        sources: [
          { id: 'trust', title: 'Trust Metrics 2023', link: 'https://example.com/trust' },
          { id: 'repro', title: 'Reproducibility Handbook' },
        ],
        createdAt: new Date(),
      },
      {
        id: '3e',
        sessionId: '3',
        role: 'user',
        parts: [{
          type: 'text',
          content: 'Can citations handle multimedia references, like datasets or videos?',
          format: 'plain',
        }],
        createdAt: new Date(),
      },
      {
        id: '3f',
        sessionId: '3',
        role: 'assistant',
        parts: [{
          type: 'text',
          format: 'markdown',
          content: [
            "Yes. Provide a descriptive title plus the media link [^dataset]. ",
            "Structured `sources` metadata ensures the UI can render consistent labels[^video].",
          ].join(''),
        }],
        sources: [
          { id: 'dataset', title: 'Global Dataset Catalog (2024)', link: 'https://example.com/dataset' },
          { id: 'video', title: 'Lecture on Multimedia Citations', link: 'https://example.com/video' },
        ],
        createdAt: new Date(),
      },
      {
        id: '3g',
        sessionId: '3',
        role: 'user',
        parts: [{
          type: 'text',
          content: 'This is helpful. Could you summarize best practices for citing multiple formats?',
          format: 'plain',
        }],
        createdAt: new Date(),
      },
      {
        id: '3h',
        sessionId: '3',
        role: 'assistant',
        parts: [{
          type: 'text',
          format: 'markdown',
          content: [
            "Absolutely:\n",
            "- Always label the citation clearly[^clear].\n",
            "- Provide context on the resource type[^resource].\n",
            "- Keep references ordered and consistent[^ordered].\n\n",
            "[^clear]: Clarity in References — https://example.com/clarity\n",
            "[^resource]: Resource Type Guidelines\n",
            "[^ordered]: Ordered References Whitepaper\n",
          ].join(''),
        }],
        sources: [
          { id: 'clear', title: 'Clarity in References', link: 'https://example.com/clarity' },
          { id: 'resource', title: 'Resource Type Guidelines' },
          { id: 'ordered', title: 'Ordered References Whitepaper' },
        ],
        createdAt: new Date(),
      },
    ],
    markdown: [
      {
        id: 'mk1',
        sessionId: 'markdown',
        role: 'user',
        parts: [
          {
            type: 'text',
            content: 'Show me a sample markdown response',
            format: 'plain',
          },
        ],
        createdAt: new Date(),
      },
      {
        id: 'mk2',
        sessionId: 'markdown',
        role: 'assistant',
        parts: [
          {
            type: 'text',
            format: 'markdown',
            content: [
              `# Markdown Editor Demo

Welcome to Morkdown! This document showcases various markdown features.

## Tables

| Language | Year | Paradigm |
|----------|------|----------|
| Python   | 1991 | Multi-paradigm |
| Java     | 1995 | Object-oriented |
| JavaScript | 1995 | Multi-paradigm |
| Rust     | 2010 | Multi-paradigm |

## Mixed Lists

1. **Programming Languages**
   - Python
     - Easy to learn
     - Great for data science
   - Java
     - Enterprise standard
     - Strong typing
2. **Frameworks**
   - Vue.js
   - React
   - Angular
3. **Tools**
   1. Git
   2. Docker
   3. VS Code

## Code Examples

### Python

\`\`\`python
def fibonacci(n):
    """Generate Fibonacci sequence up to n terms."""
    a, b = 0, 1
    result = []
    for _ in range(n):
        result.append(a)
        a, b = b, a + b
    return result

# Example usage
print(fibonacci(10))
\`\`\`

### Java

\`\`\`java
public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");

        // Fibonacci example
        int n = 10;
        for (int i = 0; i < n; i++) {
            System.out.println(fibonacci(i));
        }
    }

    public static int fibonacci(int n) {
        if (n <= 1) return n;
        return fibonacci(n - 1) + fibonacci(n - 2);
    }
}
\`\`\`

## Mermaid Diagrams

### Sequence Diagram

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Browser
    participant Server
    participant Database

    User->>Browser: Click "Save"
    Browser->>Server: POST /api/save
    Server->>Database: INSERT data
    Database-->>Server: Success
    Server-->>Browser: 200 OK
    Browser-->>User: Show confirmation
\`\`\`

### Flow Chart

\`\`\`mermaid
graph TD
    A[Start] --> B{Is Valid?}
    B -->|Yes| C[Process Data]
    B -->|No| D[Show Error]
    C --> E{Save Success?}
    E -->|Yes| F[Display Success]
    E -->|No| D
    D --> G[End]
    F --> G
\`\`\`

## Math Examples

Inline math such as $e^{i\\pi} + 1 = 0$ works too.

Block math:

$$
\\int_0^\\infty e^{-x^2}\\,dx = \\frac{\\sqrt{\\pi}}{2}
$$

## Additional Features

- **Bold text**
- *Italic text*
- \`inline code\`
- ~~Strikethrough~~

Research influences: Classic HCI references[^source-hci] and modern productivity studies[^source-productivity].

Footnote syntax example with inline references[^first]. Footnote can reference another note[^second].

[^first]: Initial citation that mentions [Morkdown Docs](https://example.com/morkdown).
[^second]: Follow-up citation referencing the earlier note[^first].

> This is a blockquote.
> It can span multiple lines.

---

Start editing to see live preview! 🚀`,
            ].join('\n'),
          },
        ],
        sources: [
          {
            id: 'source-hci',
            title: 'ACM HCI Handbook',
            link: 'https://dl.acm.org/doi/10.1145/hci-handbook',
          },
          {
            id: 'source-productivity',
            title: 'Note Taking Productivity Patterns (2024)',
            link: 'https://example.com/productivity-patterns',
          },
        ],
        createdAt: new Date(),
      },
    ],
    'adaptive-cards': [
      {
        id: 'ac1',
        sessionId: 'adaptive-cards',
        role: 'user',
        parts: [{ type: 'text', content: 'Show me a greeting card', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: 'ac2',
        sessionId: 'adaptive-cards',
        role: 'assistant',
        parts: [{
          type: 'adaptive-card',
          template: structuredClone(acTestTemplate) as AdaptiveCardSchema,
          data: structuredClone(acTestData),
        }],
        createdAt: new Date(),
      },
      {
        id: 'ac3',
        sessionId: 'adaptive-cards',
        role: 'user',
        parts: [{ type: 'text', content: 'Show me an activity update card', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: 'ac4',
        sessionId: 'adaptive-cards',
        role: 'assistant',
        parts: [{
          type: 'adaptive-card',
          template: structuredClone(acActivityUpdateTemplate) as AdaptiveCardSchema,
          data: structuredClone(acActivityUpdateData),
        }],
        createdAt: new Date(),
      },
      {
        id: 'ac5',
        sessionId: 'adaptive-cards',
        role: 'user',
        parts: [{ type: 'text', content: 'Show me an expense report card', format: 'plain' }],
        createdAt: new Date(),
      },
      {
        id: 'ac6',
        sessionId: 'adaptive-cards',
        role: 'assistant',
        parts: [{
          type: 'adaptive-card',
          template: structuredClone(acExpenseReportTemplate) as AdaptiveCardSchema,
          data: structuredClone(acExpenseReportData),
        }],
        createdAt: new Date(),
      },
    ],
  }

  async getSessions(_signal?: AbortSignal, options?: { botId?: string; limit?: number }): Promise<Session[]> {
    await delay(200)
    return options?.botId
      ? this.sessions.filter(session => session.botId === options.botId).map(session => ({ ...session }))
      : [...this.sessions]
  }

  async getMostRecentSession(botId: string, signal?: AbortSignal): Promise<Session | null> {
    const sessions = await this.getSessions(signal, { botId })
    return sessions[0] ?? null
  }

  async getMessages(_botId: string, sessionId: string, _signal?: AbortSignal): Promise<Message[]> {
    await delay(300)
    console.log('[MockChatClient] getMessages returning for session:', sessionId)
    return [...(this.messages[sessionId] ?? [])]
  }

  async getSession(botId: string, sessionId: string, _signal?: AbortSignal): Promise<Session> {
    await delay(120)
    const session = this.sessions.find(s => s.id === sessionId && s.botId === botId)
    if (!session) {
      throw new Error(`Session not found: ${botId}/${sessionId}`)
    }
    return { ...session }
  }

  async claimAnonymousSession(botId: string, sessionId: string): Promise<Session> {
    return this.getSession(botId, sessionId)
  }

  async createSession(botId: string, title?: string): Promise<Session> {
    await delay(200)
    const session: Session = {
      id: crypto.randomUUID(),
      title: title ?? 'New Chat',
      botId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }
    this.sessions.unshift(session)
    this.messages[session.id] = []
    return session
  }

  async greet(botId: string): Promise<{ session: Session; message: Message }> {
    await delay(500)
    const session: Session = {
      id: crypto.randomUUID(),
      title: 'New Chat',
      botId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    const useCard = Math.random() < 0.7
    const message: Message = {
      id: crypto.randomUUID(),
      sessionId: session.id,
      role: 'assistant',
      parts: useCard
        ? [
            {
              type: 'adaptive-card',
              template: structuredClone(greetingTemplate) as AdaptiveCardSchema,
              data: structuredClone(greetingData),
            },
          ]
        : [
            {
              type: 'text',
              content: "Hi there! I'm ready to help—ask me anything.",
              format: 'plain',
            },
          ],
      createdAt: new Date(),
    }

    this.sessions.unshift(session)
    this.messages[session.id] = [message]

    return { session, message }
  }

  async sendMessage(sessionId: string, parts: MessagePart[]): Promise<Message> {
    // Store user message
    const userMsg: Message = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'user',
      parts,
      createdAt: new Date(),
    }
    const sessionMessages = this.messages[sessionId] ?? (this.messages[sessionId] = [])
    sessionMessages.push(userMsg)

    // Simulate assistant thinking
    await delay(3000)

    // Add and return assistant message
    const responseIndex = Math.floor(Math.random() * responses.length)
    const response = responses[responseIndex] ?? responses[0]
    const useCard = Math.random() < 0.3
    const assistantMsg: Message = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'assistant',
      parts: useCard
        ? [
            {
              type: 'adaptive-card',
              template: structuredClone(greetingTemplate) as AdaptiveCardSchema,
              data: structuredClone(greetingData),
            },
          ]
        : [{ type: 'text', content: response, format: 'plain' }],
      createdAt: new Date(),
    }
    sessionMessages.push(assistantMsg)

    return assistantMsg
  }

  async deleteSession(botId: string, sessionId: string): Promise<void> {
    await delay(150)
    const session = this.sessions.find(s => s.id === sessionId && s.botId === botId)
    if (!session) return
    this.sessions = this.sessions.filter(s => s.id !== sessionId)
    delete this.messages[sessionId]
  }

  async updateSession(botId: string, sessionId: string, updates: { title?: string; starred?: boolean }): Promise<Session> {
    await delay(150)
    const session = this.sessions.find(s => s.id === sessionId && s.botId === botId)
    if (!session) {
      throw new Error(`Session not found: ${botId}/${sessionId}`)
    }
    if (updates.title !== undefined) {
      session.title = updates.title
    }
    if (updates.starred !== undefined) {
      session.starred = updates.starred
    }
    session.updatedAt = new Date()
    return { ...session }
  }

  async toggleStar(botId: string, sessionId: string): Promise<{ starred: boolean; limitReached?: boolean }> {
    await delay(100)
    const session = this.sessions.find(s => s.id === sessionId && s.botId === botId)
    if (!session) {
      return { starred: false }
    }
    
    const starredCount = this.sessions.filter(s => s.starred).length
    const isCurrentlyStarred = session.starred ?? false
    
    // If trying to star and already at limit
    if (!isCurrentlyStarred && starredCount >= 10) {
      return { starred: false, limitReached: true }
    }
    
    session.starred = !isCurrentlyStarred
    return { starred: session.starred }
  }
}
